import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Checkbox,
  TextField,
  InputAdornment,
  Box,
  Typography,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import type { FlowBiMap, FlowDisplayItem } from '../utils/FlowBiMap';

const ROW_HEIGHT = 36;
const CONTAINER_HEIGHT = 400;
const VIRTUALIZATION_THRESHOLD = 100;

interface DomainFlowTreeDialogProps {
  open: boolean;
  onClose: () => void;
  flowBiMap: FlowBiMap;
  companyIds: number[];
  branchIds: number[];
  selectedFlows: string[];
  onApply: (domains: string[], flows: string[]) => void;
}

type VirtualRow =
  | { type: 'domain'; domain: string; flowCount: number }
  | { type: 'flow'; flow: string; domain: string };

// Pure function — no need to be a hook
function getDomainCheckState(grouped: Map<string, FlowDisplayItem[]>, localFlows: Set<string>, domain: string) {
  const flows = grouped.get(domain) ?? [];
  const selectedCount = flows.filter((f) => localFlows.has(f.flow)).length;
  if (selectedCount === 0) return { checked: false, indeterminate: false };
  if (selectedCount === flows.length) return { checked: true, indeterminate: false };
  return { checked: false, indeterminate: true };
}

const DomainFlowTreeDialog: FC<DomainFlowTreeDialogProps> = ({
  open,
  onClose,
  flowBiMap,
  companyIds,
  branchIds,
  selectedFlows,
  onApply,
}) => {
  const [localFlows, setLocalFlows] = useState<Set<string>>(new Set(selectedFlows));
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [scrollTop, setScrollTop] = useState(0);

  // Reset local state every time the dialog opens
  useEffect(() => {
    if (open) {
      setLocalFlows(new Set(selectedFlows));
      setExpandedDomains(new Set());
      setSearch('');
      setDebouncedSearch('');
      setScrollTop(0);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 200);
    return () => {
      clearTimeout(timer);
    };
  }, [search]);

  const grouped = useMemo(
    () => flowBiMap.getFlowsGroupedByDomain(companyIds, branchIds),
    [flowBiMap, companyIds, branchIds]
  );

  const filteredGrouped = useMemo(() => {
    if (!debouncedSearch.trim()) return grouped;
    const lower = debouncedSearch.toLowerCase();
    const result = new Map<string, FlowDisplayItem[]>();
    grouped.forEach((flows, domain) => {
      if (domain.toLowerCase().includes(lower)) {
        result.set(domain, flows);
      } else {
        const matched = flows.filter((f) => f.flow.toLowerCase().includes(lower));
        if (matched.length > 0) result.set(domain, matched);
      }
    });
    return result;
  }, [grouped, debouncedSearch]);

  const rows = useMemo<VirtualRow[]>(() => {
    const result: VirtualRow[] = [];
    filteredGrouped.forEach((flows, domain) => {
      result.push({ type: 'domain', domain, flowCount: flows.length });
      if (expandedDomains.has(domain)) {
        flows.forEach((f) => {
          result.push({ type: 'flow', flow: f.flow, domain });
        });
      }
    });
    return result;
  }, [filteredGrouped, expandedDomains]);

  const toggleDomainExpand = useCallback((domain: string) => {
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      next.has(domain) ? next.delete(domain) : next.add(domain);
      return next;
    });
  }, []);

  // Unified toggle with functional updater — no stale closure over localFlows
  const toggle = useCallback(
    (type: 'domain' | 'flow', key: string) => {
      setLocalFlows((prev) => {
        const next = new Set(prev);
        if (type === 'domain') {
          // Operate on all flows of the domain, not just visible ones
          const flowKeys = (grouped.get(key) ?? []).map((f) => f.flow);
          const allSelected = flowKeys.every((f) => next.has(f));
          flowKeys.forEach((f) => {
            allSelected ? next.delete(f) : next.add(f);
          });
        } else {
          next.has(key) ? next.delete(key) : next.add(key);
        }
        return next;
      });
    },
    [grouped]
  );

  const handleApply = () => {
    // Derive selected domains from the grouped map — correct under current company/branch scope
    const domains: string[] = [];
    grouped.forEach((flows, domain) => {
      if (flows.some((f) => localFlows.has(f.flow))) domains.push(domain);
    });
    onApply(domains, [...localFlows]);
    onClose();
  };

  const useVirtualization = rows.length > VIRTUALIZATION_THRESHOLD;
  const totalSelected = localFlows.size;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Domains & Flows {totalSelected > 0 && `(${totalSelected} selected)`}</DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          placeholder="Search domains or flows..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {useVirtualization ? (
          <Box
            sx={{ height: CONTAINER_HEIGHT, overflow: 'auto', position: 'relative' }}
            onScroll={(e) => {
              setScrollTop(e.currentTarget.scrollTop);
            }}
          >
            <Box sx={{ height: rows.length * ROW_HEIGHT, position: 'relative' }}>
              {(() => {
                const startIndex = Math.floor(scrollTop / ROW_HEIGHT);
                const endIndex = Math.min(rows.length, startIndex + Math.ceil(CONTAINER_HEIGHT / ROW_HEIGHT) + 2);
                return rows.slice(startIndex, endIndex).map((row, i) => {
                  const top = (startIndex + i) * ROW_HEIGHT;
                  if (row.type === 'domain') {
                    const { checked, indeterminate } = getDomainCheckState(grouped, localFlows, row.domain);
                    return (
                      <Box
                        key={`d-${row.domain}`}
                        sx={{
                          position: 'absolute',
                          top,
                          left: 0,
                          right: 0,
                          height: ROW_HEIGHT,
                          display: 'flex',
                          alignItems: 'center',
                          px: 1,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() => {
                          toggleDomainExpand(row.domain);
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={checked}
                          indeterminate={indeterminate}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggle('domain', row.domain);
                          }}
                          sx={{ p: 0, mr: 0.5 }}
                        />
                        <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>
                          {row.domain}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                          {row.flowCount} flows
                        </Typography>
                        {expandedDomains.has(row.domain) ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </Box>
                    );
                  }
                  return (
                    <Box
                      key={`f-${row.flow}`}
                      sx={{
                        position: 'absolute',
                        top,
                        left: 0,
                        right: 0,
                        height: ROW_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        pl: 4,
                        pr: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                      onClick={() => {
                        toggle('flow', row.flow);
                      }}
                    >
                      <Checkbox size="small" checked={localFlows.has(row.flow)} sx={{ p: 0, mr: 0.5 }} />
                      <Typography variant="body2">{row.flow}</Typography>
                    </Box>
                  );
                });
              })()}
            </Box>
          </Box>
        ) : (
          <Box sx={{ maxHeight: CONTAINER_HEIGHT, overflow: 'auto' }}>
            <List dense disablePadding>
              {[...filteredGrouped.entries()].map(([domain, flows]) => {
                const { checked, indeterminate } = getDomainCheckState(grouped, localFlows, domain);
                const isExpanded = expandedDomains.has(domain);
                return (
                  <Box key={domain}>
                    <ListItemButton
                      onClick={() => {
                        toggleDomainExpand(domain);
                      }}
                      dense
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Checkbox
                          edge="start"
                          size="small"
                          checked={checked}
                          indeterminate={indeterminate}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggle('domain', domain);
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary={domain} secondary={`${flows.length} flows`} />
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List dense disablePadding sx={{ pl: 4 }}>
                        {flows.map((flow) => (
                          <ListItemButton
                            key={flow.flow}
                            dense
                            onClick={() => {
                              toggle('flow', flow.flow);
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <Checkbox edge="start" size="small" checked={localFlows.has(flow.flow)} />
                            </ListItemIcon>
                            <ListItemText primary={flow.flow} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                );
              })}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleApply} variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DomainFlowTreeDialog;
